import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, X, AlertTriangle, Clock, CheckCircle, XCircle,
  FileText, Calendar, DollarSign, User, Eye, Loader2, RefreshCw,
  Building, CreditCard, MessageSquare
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/badge";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchRefundRequests, updateRefundRequest } from "../redux/Slices/RefundRequestSlice";
import { RefundRequestStatusEnum, NotificationTypeEnum, NotificationPriorityEnum, NotificationStatusEnum, RoleEnum } from "../modules/shared/model";
import { GlobalService } from "../modules";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import { escrowAPI, EscrowStatusEnum } from "../services/escrowAPI";
import { createUserNotificationAction } from "../redux/Actions/UserNotificationActions";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { useTranslation } from "react-i18next";

interface RefundRequestsPageProps {
  isClient?: boolean;
}

const RefundRequestsPage: React.FC<RefundRequestsPageProps> = ({ isClient = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state: any) => state.auth);
  const { refundRequests, loading, error, pagination } = useAppSelector((state: any) => state.refundRequests);

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [contractEscrows, setContractEscrows] = useState<any[]>([]);
  const [loadingEscrows, setLoadingEscrows] = useState(false);

  // Determine if user is professional based on route or prop
  const isProfessional = !isClient;

  // Fetch refund requests
  const fetchRequests = useCallback(async () => {
    if (!user?.id) return;

    try {
      const queryParams: any = {
        page: currentPage,
        limit,
        filters: {
          userId: user.id
        }
      };

      // Add status filter
      if (statusFilter !== "all") {
        queryParams.filters.statusIds = [statusFilter];
      } else {
        // When "all" is selected, don't include statusIds filter to get all statuses
        delete queryParams.filters.statusIds;
      }

      // Add type filter
      if (typeFilter !== "all") {
        queryParams.type = Number(typeFilter);
      }

      // Add search query
      if (searchQuery && searchQuery.length >= 3) {
        queryParams.keyword = searchQuery;
      }

      // Add sorting
      const sortOptions = [];
      switch (sortBy) {
        case "newest":
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
          break;
        case "oldest":
          sortOptions.push({ orderBy: "createdAt", order: "ASC" });
          break;
        case "amount_high":
          sortOptions.push({ orderBy: "amount", order: "DESC" });
          break;
        case "amount_low":
          sortOptions.push({ orderBy: "amount", order: "ASC" });
          break;
        default:
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
      }
      queryParams.sort = JSON.stringify(sortOptions);

      await dispatch(fetchRefundRequests(queryParams));
    } catch (error) {
      console.error("Failed to fetch refund requests:", error);
    }
  }, [dispatch, user?.id, currentPage, statusFilter, typeFilter, searchQuery, sortBy]);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Handle search with debounce
  useEffect(() => {
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      const timeoutId = setTimeout(() => {
        fetchRequests();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === "status") {
      setStatusFilter(value);
    } else if (filterType === "type") {
      setTypeFilter(value);
    } else if (filterType === "sort") {
      setSortBy(value);
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle approve refund request (Professional only)
  const handleApproveRefund = async (refundRequest: any) => {
    if (!isProfessional) return;
    
    // Only allow approving if status is Active
    if (Number(refundRequest.status?.id) !== RefundRequestStatusEnum.Active) {
      GlobalService.showToast("error", t("toast.titles.error"), t("refundRequestsPage.onlyActiveCanApprove"));
      return;
    }

    setActionLoading(refundRequest.id);
    try {
      await dispatch(updateRefundRequest({
        id: refundRequest.id,
        data: {
          status: { id: RefundRequestStatusEnum.Approved }
        }
      })).unwrap();

      GlobalService.showToast("success", t("toast.titles.success"), t("refundRequestsPage.refundApprovedSuccess"));
      
      // Send notification to client
      try {
        const clientId = refundRequest.contract?.client?.id;
        if (clientId) {
          await createUserNotificationAction({
            user: { id: clientId },
            type: { id: NotificationTypeEnum["In-App"] },
            priority: { id: NotificationPriorityEnum.High },
            status: { id: NotificationStatusEnum.Unread },
            title: t("refundRequestsPage.approvalNotificationTitle"),
            message: t("refundRequestsPage.approvalNotificationMessage", { 
              id: refundRequest.id, 
              contract: refundRequest.contract?.title || "Contract" 
            }),
            url: "/client/refund-requests"
          });
          console.log("Approval notification sent to client");
        }
      } catch (notificationError) {
        console.error("Error sending approval notification:", notificationError);
        // Don't fail the whole operation if notification fails
      }
      
      setShowDetailsModal(false);
      fetchRequests(); // Refresh the list
    } catch (error: any) {
      console.error("Failed to approve refund request:", error);
      GlobalService.showToast("error", t("toast.titles.error"), t("refundRequestsPage.failedToApprove", { error: error?.message || "Unknown error" }));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject refund request (Professional only)
  const handleRejectRefund = async (refundRequest: any) => {
    if (!isProfessional || !rejectReason.trim()) {
      if (!rejectReason.trim()) {
        GlobalService.showToast("error", t("toast.titles.error"), t("refundRequestsPage.provideRejectionReason"));
      }
      return;
    }
    
    // Only allow rejecting if status is Active
    if (Number(refundRequest.status?.id) !== RefundRequestStatusEnum.Active) {
      GlobalService.showToast("error", t("toast.titles.error"), t("refundRequestsPage.onlyActiveCanReject"));
      return;
    }

    setActionLoading(refundRequest.id);
    try {
      await dispatch(updateRefundRequest({
        id: refundRequest.id,
        data: {
          status: { id: RefundRequestStatusEnum.Rejected },
          rejectReason: rejectReason.trim()
        }
      })).unwrap();

      GlobalService.showToast("success", t("toast.titles.success"), t("refundRequestsPage.refundRejectedSuccess"));
      
      // Send notification to client
      try {
        const clientId = refundRequest.contract?.client?.id;
        if (clientId) {
          await createUserNotificationAction({
            user: { id: clientId },
            type: { id: NotificationTypeEnum["In-App"] },
            priority: { id: NotificationPriorityEnum.High },
            status: { id: NotificationStatusEnum.Unread },
            title: t("refundRequestsPage.rejectionNotificationTitle"),
            message: t("refundRequestsPage.rejectionNotificationMessage", { 
              id: refundRequest.id, 
              contract: refundRequest.contract?.title || "Contract",
              reason: rejectReason.trim()
            }),
            url: "/client/refund-requests"
          });
          console.log("Rejection notification sent to client");
        }
      } catch (notificationError) {
        console.error("Error sending rejection notification:", notificationError);
        // Don't fail the whole operation if notification fails
      }
      
      setShowRejectDialog(false);
      setRejectReason("");
      setShowDetailsModal(false);
      fetchRequests(); // Refresh the list
    } catch (error: any) {
      console.error("Failed to reject refund request:", error);
      GlobalService.showToast("error", t("toast.titles.error"), t("refundRequestsPage.failedToReject", { error: error?.message || "Unknown error" }));
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch escrows for contract
  const fetchContractEscrows = async (contractId: string) => {
    if (!contractId) return;
    
    setLoadingEscrows(true);
    try {
      const escrows = await escrowAPI.getEscrowsByContract(contractId);
      setContractEscrows(escrows || []);
    } catch (error) {
      console.error("Failed to fetch contract escrows:", error);
      setContractEscrows([]);
    } finally {
      setLoadingEscrows(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowDetailsModal(false);
    setContractEscrows([]);
  };

  // Handle opening detail modal
  const handleOpenDetailModal = (request: any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
    
    // Fetch escrows if request is completed or refunded
    if (request?.contract?.id && 
        (Number(request.status?.id) === RefundRequestStatusEnum.Completed || 
         Number(request.status?.id) === RefundRequestStatusEnum.Refunded)) {
      fetchContractEscrows(request.contract.id);
    }
  };

  // Get status badge info
  const getStatusBadgeInfo = useCallback((statusId: number) => {
    const numericStatusId = Number(statusId);
    switch (numericStatusId) {
      case RefundRequestStatusEnum.Active:
        return {
          text: t("refundRequestsPage.pending"),
          className: "bg-yellow-100 text-yellow-800"
        };
      case RefundRequestStatusEnum.Approved:
        return {
          text: t("refundRequestsPage.approved"),
          className: "bg-green-100 text-green-800"
        };
      case RefundRequestStatusEnum.Rejected:
        return {
          text: t("refundRequestsPage.rejected"),
          className: "bg-red-100 text-red-800"
        };
      case RefundRequestStatusEnum.Completed:
        return {
          text: t("refundRequestsPage.completed"),
          className: "bg-blue-100 text-blue-800"
        };
      case RefundRequestStatusEnum.Refunded:
        return {
          text: t("refundRequestsPage.refunded"),
          className: "bg-purple-100 text-purple-800"
        };
      case RefundRequestStatusEnum.In_Progress:
        return {
          text: t("refundRequestsPage.inProgress"),
          className: "bg-indigo-100 text-indigo-800"
        };
      default:
        return {
          text: "Unknown",
          className: "bg-gray-100 text-gray-800"
        };
    }
  }, [t]);

  // Get type badge info
  const getTypeBadgeInfo = useCallback((typeId: number) => {
    const numericTypeId = Number(typeId);
    switch (numericTypeId) {
      case 1:
        return {
          text: t("refundRequestsPage.fullRefund"),
          className: "bg-blue-100 text-blue-800"
        };
      case 2:
        return {
          text: t("refundRequestsPage.partialRefund"),
          className: "bg-purple-100 text-purple-800"
        };
      default:
        return {
          text: "Unknown Type",
          className: "bg-gray-100 text-gray-800"
        };
    }
  }, [t]);

  // Calculate stats
  const stats = useMemo(() => {
    // Use totalCount from backend for total, but calculate others from current page data
    const total = pagination.totalCount || 0;
    const pending = refundRequests.filter((req: any) => Number(req.status?.id) === RefundRequestStatusEnum.Active).length;
    const approved = refundRequests.filter((req: any) => Number(req.status?.id) === RefundRequestStatusEnum.Approved).length;
    const rejected = refundRequests.filter((req: any) => Number(req.status?.id) === RefundRequestStatusEnum.Rejected).length;
    const completed = refundRequests.filter((req: any) => Number(req.status?.id) === RefundRequestStatusEnum.Completed).length;
    const totalAmount = refundRequests.reduce((sum: number, req: any) => sum + (req.amount || 0), 0);

    return { total, pending, approved, rejected, completed, totalAmount };
  }, [refundRequests, pagination.totalCount]);

  // Loading spinner component
  const LoadingSpinner = () => <Loader2 className="h-4 w-4 animate-spin" />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      {isProfessional ? <SharedHeader4 /> : <SharedHeader5 />}

      <main className="flex-1 max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 w-full">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
            {isProfessional ? t("refundRequestsPage.titleProfessional") : t("refundRequestsPage.titleClient")}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            {isProfessional 
              ? t("refundRequestsPage.subtitleProfessional")
              : t("refundRequestsPage.subtitleClient")
            }
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{t("refundRequestsPage.total")}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {loading ? <LoadingSpinner /> : stats.total}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{t("refundRequestsPage.pending")}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {loading ? <LoadingSpinner /> : stats.pending}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{t("refundRequestsPage.approved")}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {loading ? <LoadingSpinner /> : stats.approved}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{t("refundRequestsPage.completed")}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {loading ? <LoadingSpinner /> : stats.completed}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{t("refundRequestsPage.rejected")}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  {loading ? <LoadingSpinner /> : stats.rejected}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("refundRequestsPage.searchPlaceholder")}
                  className="w-full pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <select
                value={statusFilter}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="px-3 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t("refundRequestsPage.allStatus")}</option>
                <option value={RefundRequestStatusEnum.Active}>{t("refundRequestsPage.pending")}</option>
                <option value={RefundRequestStatusEnum.In_Progress}>{t("refundRequestsPage.inProgress")}</option>
                <option value={RefundRequestStatusEnum.Approved}>{t("refundRequestsPage.approved")}</option>
                <option value={RefundRequestStatusEnum.Completed}>{t("refundRequestsPage.completed")}</option>
                <option value={RefundRequestStatusEnum.Refunded}>{t("refundRequestsPage.refunded")}</option>
                <option value={RefundRequestStatusEnum.Rejected}>{t("refundRequestsPage.rejected")}</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="px-3 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t("refundRequestsPage.allTypes")}</option>
                <option value="1">{t("refundRequestsPage.fullRefund")}</option>
                <option value="2">{t("refundRequestsPage.partialRefund")}</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => handleFilterChange("sort", e.target.value)}
                className="px-3 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">{t("refundRequestsPage.newestFirst")}</option>
                <option value="oldest">{t("refundRequestsPage.oldestFirst")}</option>
                <option value="amount_high">{t("refundRequestsPage.highestAmount")}</option>
                <option value="amount_low">{t("refundRequestsPage.lowestAmount")}</option>
              </select>

              <Button
                onClick={() => fetchRequests()}
                disabled={loading}
                className="flex items-center gap-2 text-sm sm:text-base px-3 py-2 sm:py-3"
              >
                {loading ? <LoadingSpinner /> : <RefreshCw className="h-4 w-4" />}
                {t("refundRequestsPage.refresh")}
              </Button>
            </div>
          </div>
        </Card>

        {/* Refund Requests List */}
        <div className="space-y-3 sm:space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="mt-2 text-gray-600">{t("refundRequestsPage.loadingRefundRequests")}</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">{t("refundRequestsPage.errorLoading", { error })}</p>
              <Button onClick={fetchRequests} className="mt-4">
                {t("refundRequestsPage.tryAgain")}
              </Button>
            </div>
          ) : refundRequests && refundRequests.length > 0 ? (
            refundRequests.map((request: any) => {
              const statusInfo = getStatusBadgeInfo(Number(request.status?.id));
              const typeInfo = getTypeBadgeInfo(Number(request.type?.id));

              return (
                <Card key={request.id} className="p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                        <div className="flex-1">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {t("refundRequestsPage.refundRequestNumber", { id: request.id })}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {t("refundRequestsPage.contract", { title: request.contract?.title || "N/A" })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={statusInfo.className} variant={undefined}>
                            {statusInfo.text}
                          </Badge>
                          <Badge className={typeInfo.className} variant={undefined}>
                            {typeInfo.text}
                          </Badge>
                          <Button
                            variant="outline"
                            onClick={() => handleOpenDetailModal(request)}
                            className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 ml-2"
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">{t("refundRequestsPage.viewDetails")}</span>
                            <span className="sm:hidden">{t("refundRequestsPage.view")}</span>
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.amount")}</span>
                          <span className="ml-1 font-medium text-red-600">
                            {getDynamicCurrencySymbol()}{request.amount?.toFixed(2) || "0.00"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.requested")}</span>
                          <span className="ml-1">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.requestedBy")}</span>
                          <span className="ml-1">
                            {isProfessional ? request.client?.name || t("refundRequestsPage.client") : t("refundRequestsPage.you")}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.reason")}</span>
                          <span className="ml-1 break-words">
                            {request.reason || t("refundRequestsPage.noReasonProvided")}
                          </span>
                        </div>
                      </div>

                      {request.rejectReason && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <span className="text-xs sm:text-sm font-medium text-red-800">{t("refundRequestsPage.rejectionReason")}</span>
                          <p className="text-xs sm:text-sm text-red-700 mt-1">{request.rejectReason}</p>
                        </div>
                      )}
                    </div>

                  </div>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-8 sm:py-12">
              <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">{t("refundRequestsPage.noRefundRequestsFound")}</h3>
              <p className="text-sm sm:text-base text-gray-600">
                {isProfessional 
                  ? t("refundRequestsPage.noRefundRequestsProfessional")
                  : t("refundRequestsPage.noRefundRequestsClient")
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {(refundRequests && refundRequests.length > 0) && (
          <div className="mt-6 sm:mt-8">
            {/* Pagination Info */}
            <div className="flex justify-center mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm text-gray-600 text-center px-2">
                {t("refundRequestsPage.showing", { 
                  start: ((currentPage - 1) * limit) + 1, 
                  end: Math.min(currentPage * limit, pagination.totalCount), 
                  total: pagination.totalCount 
                })}
              </p>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex justify-center gap-1 sm:gap-2 flex-wrap">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
              >
                {t("refundRequestsPage.previous")}
              </Button>
              
              {/* Page Numbers */}
              {(() => {
                const totalPages = Math.ceil(pagination.totalCount / limit);
                const maxVisiblePages = 5;
                let startPage, endPage;

                if (totalPages <= maxVisiblePages) {
                  startPage = 1;
                  endPage = totalPages;
                } else {
                  const halfVisible = Math.floor(maxVisiblePages / 2);
                  if (currentPage <= halfVisible) {
                    startPage = 1;
                    endPage = maxVisiblePages;
                  } else if (currentPage + halfVisible >= totalPages) {
                    startPage = totalPages - maxVisiblePages + 1;
                    endPage = totalPages;
                  } else {
                    startPage = currentPage - halfVisible;
                    endPage = currentPage + halfVisible;
                  }
                }

                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      variant={i === currentPage ? "default" : "outline"}
                      className={`text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 ${i === currentPage ? "bg-blue-600 text-white" : ""}`}
                    >
                      {i}
                    </Button>
                  );
                }
                return pages;
              })()}

              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                variant="outline"
                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
              >
                {t("refundRequestsPage.next")}
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <SharedFooter3 />

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleModalClose();
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {t("refundRequestsPage.refundRequestDetails")}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {t("refundRequestsPage.requestNumber", { id: selectedRequest.id })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleModalClose}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-140px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Left Column - Request Details */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Status and Type */}
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusBadgeInfo(Number(selectedRequest.status?.id)).className} variant={undefined}>
                        {getStatusBadgeInfo(Number(selectedRequest.status?.id)).text}
                      </Badge>
                      <Badge className={getTypeBadgeInfo(Number(selectedRequest.type?.id)).className} variant={undefined}>
                        {getTypeBadgeInfo(Number(selectedRequest.type?.id)).text}
                      </Badge>
                    </div>

                    {/* Contract Information */}
                    <Card className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <Building className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">{t("refundRequestsPage.contractDetails")}</h4>
                      </div>
                      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.contractId")}</span>
                          <span className="ml-2 font-medium">{selectedRequest.contract?.id || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.contractTitle")}</span>
                          <span className="ml-2">{selectedRequest.contract?.title || "N/A"}</span>
                        </div>
                      </div>
                    </Card>

                    {/* Financial Information */}
                    <Card className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">{t("refundRequestsPage.financialDetails")}</h4>
                      </div>
                      <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-600">{t("refundRequestsPage.requestedAmount")}</span>
                          <span className="ml-2 font-bold text-red-600">
                            {getDynamicCurrencySymbol()}{selectedRequest.amount?.toFixed(2) || "0.00"}
                          </span>
                        </div>
                      </div>
                    </Card>

                    {/* Reason */}
                    <Card className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">{t("refundRequestsPage.reasonForRefund")}</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 break-words whitespace-pre-wrap">
                        {selectedRequest.reason || t("refundRequestsPage.noReasonProvided")}
                      </p>
                    </Card>
                  </div>

                  {/* Right Column - Additional Info & Actions */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Request Type */}
                    <Card className="p-3 sm:p-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">{t("refundRequestsPage.requestType")}</h4>
                      <Badge className={getTypeBadgeInfo(Number(selectedRequest.type?.id)).className} variant={undefined}>
                        {getTypeBadgeInfo(Number(selectedRequest.type?.id)).text}
                      </Badge>
                    </Card>

                    {/* Timeline */}
                    <Card className="p-3 sm:p-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">{t("refundRequestsPage.timeline")}</h4>
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="p-1.5 sm:p-2 bg-blue-100 rounded-full">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-900">{t("refundRequestsPage.requested")}</p>
                            <p className="text-xs text-gray-600">
                              {new Date(selectedRequest.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {selectedRequest.updatedAt !== selectedRequest.createdAt && (
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-green-100 rounded-full">
                              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-900">{t("refundRequestsPage.lastUpdated")}</p>
                              <p className="text-xs text-gray-600">
                                {new Date(selectedRequest.updatedAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>

                    {/* Resolution Details (if completed/refunded) */}
                    {(Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed || 
                      Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded) && (
                      <Card className="p-3 sm:p-4 border-blue-200 bg-blue-50">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                          <h4 className="text-sm sm:text-base font-semibold text-blue-900">{t("refundRequestsPage.resolutionDetails")}</h4>
                        </div>
                        
                        {loadingEscrows ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            <span className="ml-2 text-sm text-blue-700">{t("refundRequestsPage.loadingResolutionDetails")}</span>
                          </div>
                        ) : contractEscrows.length > 0 ? (
                          <div className="space-y-3">
                            {selectedRequest.resolution && (
                              <p className="text-xs sm:text-sm text-blue-700 break-words whitespace-pre-wrap mb-3">
                                {selectedRequest.resolution}
                              </p>
                            )}
                            
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-blue-900">{t("refundRequestsPage.escrowBreakdown")}</p>
                              
                              {/* Disputed Escrows */}
                              {contractEscrows.filter(e => Number(e.status?.id) === EscrowStatusEnum.disputed).length > 0 && (
                                <div className="bg-white/50 rounded p-2 space-y-1">
                                  <p className="text-xs font-medium text-gray-700">{t("refundRequestsPage.originalEscrowsDisputed")}</p>
                                  {contractEscrows
                                    .filter(e => Number(e.status?.id) === EscrowStatusEnum.disputed)
                                    .map((escrow, idx) => (
                                      <div key={escrow.id} className="text-xs text-gray-600 pl-2">
                                        • {t("refundRequestsPage.escrowNumber", { 
                                          number: idx + 1, 
                                          amount: `${getDynamicCurrencySymbol()}${escrow.amount?.toFixed(2) || "0.00"}` 
                                        })}
                                      </div>
                                    ))}
                                </div>
                              )}
                              
                              {/* Refunded Escrows (Client) */}
                              {contractEscrows.filter(e => 
                                Number(e.status?.id) === EscrowStatusEnum.refunded && 
                                e.metadata?.disputeId === selectedRequest.id
                              ).length > 0 && Number(user.role?.id) === RoleEnum.client && (
                                <div className="bg-purple-50 rounded p-2 space-y-1 border border-purple-200">
                                  <p className="text-xs font-medium text-purple-900">{t("refundRequestsPage.clientRefund")}</p>
                                  {contractEscrows
                                    .filter(e => 
                                      Number(e.status?.id) === EscrowStatusEnum.refunded && 
                                      e.metadata?.disputeId === selectedRequest.id
                                    )
                                    .map((escrow) => (
                                      <div key={escrow.id} className="space-y-1">
                                        <div className="text-xs text-purple-800 pl-2">
                                          • {t("refundRequestsPage.amountLabel", { 
                                            amount: `${getDynamicCurrencySymbol()}${escrow.amount?.toFixed(2) || "0.00"}` 
                                          })}
                                          {escrow.metadata?.percentage && (
                                            <span className="ml-1">{t("refundRequestsPage.percentage", { percentage: escrow.metadata.percentage })}</span>
                                          )}
                                        </div>
                                        {escrow.notes && (
                                          <div className="text-xs text-purple-700 pl-2 italic">
                                            {escrow.notes}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              )}
                              
                              {/* Released Escrows (Professional) */}
                              {contractEscrows.filter(e => 
                                Number(e.status?.id) === EscrowStatusEnum.released && 
                                e.metadata?.disputeId === selectedRequest.id
                              ).length > 0 && (Number(user.role?.id) === RoleEnum.professional || Number(user.role?.id) === RoleEnum.internationl_professional) && (
                                <div className="bg-green-50 rounded p-2 space-y-1 border border-green-200">
                                  <p className="text-xs font-medium text-green-900">{t("refundRequestsPage.professionalRelease")}</p>
                                  {contractEscrows
                                    .filter(e => 
                                      Number(e.status?.id) === EscrowStatusEnum.released && 
                                      e.metadata?.disputeId === selectedRequest.id
                                    )
                                    .map((escrow) => (
                                      <div key={escrow.id} className="space-y-1">
                                        <div className="text-xs text-green-800 pl-2">
                                          • {t("refundRequestsPage.grossAmount", { 
                                            amount: `${getDynamicCurrencySymbol()}${escrow.amount?.toFixed(2) || "0.00"}` 
                                          })}
                                          {escrow.metadata?.percentage && (
                                            <span className="ml-1">{t("refundRequestsPage.percentage", { percentage: escrow.metadata.percentage })}</span>
                                          )}
                                        </div>
                                        {escrow.withdrawlAmount && (
                                          <div className="text-xs text-green-800 pl-2">
                                            • {t("refundRequestsPage.withdrawalAmount", { 
                                              amount: `${getDynamicCurrencySymbol()}${escrow.withdrawlAmount.toFixed(2)}` 
                                            })}
                                            <span className="text-green-700 ml-1">{t("refundRequestsPage.afterCommission")}</span>
                                          </div>
                                        )}
                                        {escrow.metadata?.commissionDeducted && (
                                          <div className="text-xs text-green-700 pl-2">
                                            • {t("refundRequestsPage.commissionDeducted", { 
                                              amount: `${getDynamicCurrencySymbol()}${escrow.metadata.commissionDeducted.toFixed(2)}` 
                                            })}
                                          </div>
                                        )}
                                        {escrow.notes && (
                                          <div className="text-xs text-green-700 pl-2 italic">
                                            {escrow.notes}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : selectedRequest.resolution ? (
                          <p className="text-xs sm:text-sm text-blue-700 break-words whitespace-pre-wrap">
                            {selectedRequest.resolution}
                          </p>
                        ) : (
                          <p className="text-xs sm:text-sm text-blue-700">
                            {t("refundRequestsPage.noResolutionDetails")}
                          </p>
                        )}
                      </Card>
                    )}

                    {/* Rejection Reason (if rejected) */}
                    {selectedRequest.rejectReason && (
                      <Card className="p-3 sm:p-4 border-red-200 bg-red-50">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                          <h4 className="text-sm sm:text-base font-semibold text-red-900">{t("refundRequestsPage.rejectionReasonLabel")}</h4>
                        </div>
                        <p className="text-xs sm:text-sm text-red-700 break-words whitespace-pre-wrap">
                          {selectedRequest.rejectReason}
                        </p>
                      </Card>
                    )}

                    {/* Actions */}
                    <Card className="p-3 sm:p-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">{t("refundRequestsPage.actions")}</h4>
                      <div className="space-y-2 sm:space-y-3">
                        {/* Professional Actions - only show if status is Active */}
                        {(isProfessional && Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Active) && (
                          <>
                            <Button
                              onClick={() => handleApproveRefund(selectedRequest)}
                              disabled={actionLoading === selectedRequest.id}
                              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-xs sm:text-sm py-2"
                            >
                              {actionLoading === selectedRequest.id ? <LoadingSpinner /> : <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />}
                              {t("refundRequestsPage.approveRefund")}
                            </Button>
                            <Button
                              onClick={() => {
                                setShowDetailsModal(false);
                                setShowRejectDialog(true);
                              }}
                              disabled={actionLoading === selectedRequest.id}
                              variant="outline"
                              className="w-full flex items-center justify-center gap-2 border-red-300 text-red-600 hover:bg-red-50 text-xs sm:text-sm py-2"
                            >
                              <XCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                              {t("refundRequestsPage.rejectRefund")}
                            </Button>
                          </>
                        )}

                        {/* Show status message for non-active requests */}
                        {(isProfessional && Number(selectedRequest.status?.id) !== RefundRequestStatusEnum.Active) && (
                          <div className="text-center py-4">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed ? (
                                <CheckCircle className="h-5 w-5 text-blue-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded ? (
                                <CheckCircle className="h-5 w-5 text-purple-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress ? (
                                <Clock className="h-5 w-5 text-indigo-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected ? (
                                <XCircle className="h-5 w-5 text-red-600" />
                              ) : null}
                              <span className="text-sm font-medium text-gray-900">
                                {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved 
                                  ? t("refundRequestsPage.requestApproved")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed
                                    ? t("refundRequestsPage.requestCompleted")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded
                                    ? t("refundRequestsPage.requestRefunded")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress
                                    ? t("refundRequestsPage.requestInProgress")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected 
                                    ? t("refundRequestsPage.requestRejected")
                                    : t("refundRequestsPage.noActionsAvailable")
                                }
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved 
                                ? t("refundRequestsPage.approvedProcessing")
                                : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed
                                  ? t("refundRequestsPage.completedSuccessfully")
                                : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded
                                  ? t("refundRequestsPage.refundProcessed")
                                : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress
                                  ? t("refundRequestsPage.inReview")
                                : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected 
                                  ? t("refundRequestsPage.rejectedCannotModify")
                                  : t("refundRequestsPage.noActions")
                              }
                            </p>
                          </div>
                        )}

                        {/* Client View - Status Information */}
                        {!isProfessional && (
                          <div className="text-center py-3 sm:py-4">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed ? (
                                <CheckCircle className="h-5 w-5 text-blue-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded ? (
                                <CheckCircle className="h-5 w-5 text-purple-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress ? (
                                <Clock className="h-5 w-5 text-indigo-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Active ? (
                                <Clock className="h-5 w-5 text-yellow-600" />
                              ) : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected ? (
                                <XCircle className="h-5 w-5 text-red-600" />
                              ) : null}
                              <span className="text-sm font-medium text-gray-900">
                                {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Active
                                  ? t("refundRequestsPage.awaitingProfessionalReview")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved 
                                    ? t("refundRequestsPage.approvedProcessingClient")
                                    : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed
                                      ? t("refundRequestsPage.completedClient")
                                    : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded
                                      ? t("refundRequestsPage.refundedClient")
                                    : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress
                                      ? t("refundRequestsPage.underAdminReview")
                                    : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected 
                                      ? t("refundRequestsPage.requestRejected")
                                      : t("refundRequestsPage.requestStatusClient")
                                }
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Active
                                ? t("refundRequestsPage.awaitingReview")
                                : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Approved 
                                  ? t("refundRequestsPage.approvedBeingProcessed")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Completed
                                    ? t("refundRequestsPage.completedSuccessfullyClient")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Refunded
                                    ? t("refundRequestsPage.refundInAccount")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.In_Progress
                                    ? t("refundRequestsPage.underReview")
                                  : Number(selectedRequest.status?.id) === RefundRequestStatusEnum.Rejected 
                                    ? t("refundRequestsPage.rejectedSeeReason")
                                    : t("refundRequestsPage.readOnlyView")
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Dialog */}
      <AnimatePresence>
        {showRejectDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowRejectDialog(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl w-full max-w-md p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{t("refundRequestsPage.rejectRefundRequestTitle")}</h3>
                <button
                  onClick={() => setShowRejectDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    {t("refundRequestsPage.reasonForRejectionLabel")}
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t("refundRequestsPage.reasonPlaceholder")}
                    className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <Button
                    onClick={() => setShowRejectDialog(false)}
                    variant="outline"
                    className="flex-1 text-xs sm:text-sm py-2"
                  >
                    {t("refundRequestsPage.cancel")}
                  </Button>
                  <Button
                    onClick={() => handleRejectRefund(selectedRequest)}
                    disabled={!rejectReason.trim() || actionLoading === selectedRequest?.id}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 text-xs sm:text-sm py-2"
                  >
                    {actionLoading === selectedRequest?.id && <LoadingSpinner />}
                    {t("refundRequestsPage.rejectRefundButton")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RefundRequestsPage;
